import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useToast } from '@/components/ui/use-toast';
import { TrainingService } from '@/services/TrainingService';
import { TERMINAL_STATUSES, type JobEvent, type TrainingJob } from '@/types/training';
import JobStatusBadge from './JobStatusBadge';
import { formatStage } from './stageLabels';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobId: string | null;
  onJobChange?: (job: TrainingJob) => void;
};

const POLL_MS = 3000;

export default function JobDetailDialog({ open, onOpenChange, jobId, onJobChange }: Props) {
  const { toast } = useToast();
  const [job, setJob] = useState<TrainingJob | null>(null);
  const [events, setEvents] = useState<JobEvent[]>([]);
  const [cancelling, setCancelling] = useState(false);
  const [eventsExpanded, setEventsExpanded] = useState(false);
  const lastSeqRef = useRef(-1);
  const pollRef = useRef<number | null>(null);
  // Track whether we've already auto-closed for this job's completion so a
  // re-open of the dialog after closing doesn't immediately fire again.
  const autoClosedForRef = useRef<string | null>(null);

  useEffect(() => {
    if (!open || !jobId) {
      // reset on close
      setJob(null);
      setEvents([]);
      lastSeqRef.current = -1;
      if (pollRef.current) {
        window.clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return;
    }

    let cancelled = false;
    const tick = async () => {
      try {
        const [j, evs] = await Promise.all([
          TrainingService.getJob(jobId),
          TrainingService.listEvents(jobId, lastSeqRef.current === -1 ? undefined : lastSeqRef.current),
        ]);
        if (cancelled) return;
        setJob(j);
        if (onJobChange) onJobChange(j);
        // While training is in flight, auto-expand the events panel so the
        // user can watch progress; collapse it the moment we hit terminal
        // state so the dialog isn't a wall of completed log lines.
        if (!TERMINAL_STATUSES.includes(j.status)) {
          setEventsExpanded(true);
        } else {
          setEventsExpanded(false);
        }
        if (evs.length) {
          setEvents((prev) => [...prev, ...evs]);
          lastSeqRef.current = evs[evs.length - 1].seq;
        }
        if (TERMINAL_STATUSES.includes(j.status)) {
          if (pollRef.current) {
            window.clearInterval(pollRef.current);
            pollRef.current = null;
          }
          // Successful runs: celebrate briefly, then auto-close so the
          // user lands back on the dashboard where the Chat / View on HF
          // / Download buttons live. Failures stay open so the user can
          // read the error.
          if (j.status === 'completed' && autoClosedForRef.current !== jobId) {
            autoClosedForRef.current = jobId;
            toast({
              title: 'Training complete',
              description: `${j.output_name} is ready to chat with.`,
            });
            window.setTimeout(() => {
              if (!cancelled) onOpenChange(false);
            }, 1200);
          }
        }
      } catch (e: any) {
        // swallow transient errors during polling
        console.warn('[JobDetail] poll error:', e?.message);
      }
    };

    void tick();
    pollRef.current = window.setInterval(tick, POLL_MS);
    return () => {
      cancelled = true;
      if (pollRef.current) {
        window.clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [open, jobId, onJobChange, onOpenChange, toast]);

  const handleCancel = async () => {
    if (!jobId) return;
    setCancelling(true);
    try {
      await TrainingService.cancelJob(jobId);
      toast({ title: 'Cancel requested', description: 'The training node will release shortly.' });
    } catch (e: any) {
      toast({
        title: 'Cancel failed',
        description: String(e?.message || e),
        variant: 'destructive',
      });
    } finally {
      setCancelling(false);
    }
  };

  const isTerminal = job ? TERMINAL_STATUSES.includes(job.status) : false;
  const progressPct = Math.round((job?.progress ?? 0) * 100);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <span className="truncate">{job?.output_name || 'Training job'}</span>
            {job && <JobStatusBadge status={job.status} />}
          </DialogTitle>
          <DialogDescription>
            {job ? (
              <span className="text-xs">
                {job.base_model} · {job.training_type.toUpperCase()} ·{' '}
                {job.runpod_gpu_type || 'GPU'} · job {job.id.slice(0, 8)}
              </span>
            ) : (
              'Loading…'
            )}
          </DialogDescription>
        </DialogHeader>

        {job && (
          <div className="space-y-4 py-2 overflow-y-auto min-h-0">
            <div>
              <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                <span className="inline-flex items-center gap-1.5">
                  {!isTerminal && (
                    <Loader2 className="h-3 w-3 animate-spin text-canada-red" aria-hidden />
                  )}
                  {formatStage(job.stage)}
                </span>
                <span>{progressPct}%</span>
              </div>
              <Progress
                value={progressPct}
                className={!isTerminal ? 'animate-pulse' : ''}
              />
            </div>

            {!isTerminal && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground rounded-md bg-muted/40 px-3 py-2">
                <Loader2 className="h-3.5 w-3.5 animate-spin text-canada-red" aria-hidden />
                <span>
                  Live — polling every 3s. First run on a fresh node may
                  take 5–15 min to provision.
                </span>
              </div>
            )}

            {job.error && (
              <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm">
                <p className="font-medium text-destructive">{job.failure_reason || 'Failed'}</p>
                <p className="text-xs mt-1 break-all">{job.error}</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground sm:grid-cols-4">
              <div>
                <div className="font-medium text-foreground">Started</div>
                {job.started_at ? new Date(job.started_at).toLocaleTimeString() : '—'}
              </div>
              <div>
                <div className="font-medium text-foreground">Completed</div>
                {job.completed_at ? new Date(job.completed_at).toLocaleTimeString() : '—'}
              </div>
              <div>
                <div className="font-medium text-foreground">Examples</div>
                {job.dataset_examples ?? '—'}
              </div>
              <div>
                <div className="font-medium text-foreground">Node</div>
                <span className="break-all">{job.runpod_pod_id || '—'}</span>
              </div>
            </div>

            <div>
              <button
                type="button"
                onClick={() => setEventsExpanded((v) => !v)}
                className="w-full flex items-center gap-1.5 text-xs font-medium uppercase text-muted-foreground tracking-wide hover:text-foreground transition"
              >
                {eventsExpanded ? (
                  <ChevronDown className="h-3.5 w-3.5" />
                ) : (
                  <ChevronRight className="h-3.5 w-3.5" />
                )}
                Live events {events.length > 0 && <span className="text-foreground/60">({events.length})</span>}
              </button>
              {eventsExpanded && (
                <ScrollArea className="h-56 rounded-md border bg-muted/30 p-3 mt-2">
                  {events.length === 0 ? (
                    <p className="text-xs text-muted-foreground">Waiting for events…</p>
                  ) : (
                    <div className="space-y-1 font-mono text-[11px]">
                      {events.map((e) => (
                        <div key={e.seq} className="flex gap-2">
                          <span className="text-muted-foreground">
                            {new Date(e.ts).toLocaleTimeString()}
                          </span>
                          <span
                            className={
                              e.level === 'error'
                                ? 'text-destructive'
                                : e.level === 'warn'
                                  ? 'text-amber-600'
                                  : 'text-foreground'
                            }
                          >
                            [{e.event_type}]
                          </span>
                          <span className="break-all">{e.message || ''}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          {job && !isTerminal && (
            <Button variant="destructive" onClick={handleCancel} disabled={cancelling}>
              {cancelling ? 'Stopping…' : 'Stop training'}
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
